import { persistQualidocAuthCookies } from '@/lib/auth/auth-cookies';
import {
  clearPendingOAuthLoginSideEffects,
  markPendingOAuthLoginSideEffects,
} from '@/lib/auth/oauth-redirect-state';
import { isQualidocJwt } from '@/lib/auth/qualidoc-jwt';

function readOAuthTokens(): {
  accessToken: string | null;
  refreshToken: string | null;
} {
  const fromHash = new URLSearchParams(
    window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash,
  );
  const fromQuery = new URLSearchParams(window.location.search);

  return {
    accessToken: fromHash.get('access_token') || fromQuery.get('access_token'),
    refreshToken:
      fromHash.get('refresh_token') || fromQuery.get('refresh_token'),
  };
}

function stripOAuthTokensFromUrl() {
  const url = new URL(window.location.href);
  url.searchParams.delete('access_token');
  url.searchParams.delete('refresh_token');
  url.searchParams.delete('is_complete');

  if (url.hash) {
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
    hashParams.delete('access_token');
    hashParams.delete('refresh_token');
    hashParams.delete('is_complete');
    const remaining = hashParams.toString();
    url.hash = remaining ? `#${remaining}` : '';
  }

  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState(window.history.state, '', next);
}

/**
 * Reads Qualidoc OAuth tokens from hash (preferred) or query, persists cookies,
 * and strips them from the URL.
 * Returns true only when cookies were written in this call.
 * Callers must use claimPendingOAuthLoginSideEffects() for one-shot post-login work.
 */
export function persistOAuthTokensFromUrl(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const { accessToken, refreshToken } = readOAuthTokens();

  if (!accessToken || !refreshToken) {
    return false;
  }

  if (!isQualidocJwt(accessToken) || !isQualidocJwt(refreshToken)) {
    stripOAuthTokensFromUrl();
    clearPendingOAuthLoginSideEffects();
    return false;
  }

  const persisted = persistQualidocAuthCookies({
    accessToken,
    refreshToken,
    permissions: [],
  });
  stripOAuthTokensFromUrl();

  if (persisted) {
    markPendingOAuthLoginSideEffects();
  } else {
    clearPendingOAuthLoginSideEffects();
  }

  return persisted;
}
